import React, {
  Dispatch,
  ReactNode, SetStateAction, useCallback, useEffect, useMemo, useState, useRef,
} from 'react';
import { GeoLayer, Layer, Workspace } from 'types/geoserver.types';
import { Product } from 'types/app.types';
import { WMSCapabilities } from 'utils/types/WMSCapabilities.interface';
import { useGetWorkspacesQuery } from 'services/geoserver';
import { getCapabilities } from 'services/geoserver/outboundsRequest';
import { Dayjs } from 'dayjs';
import { arrayMove } from 'react-movable';
import { useLocation, useNavigate } from 'react-router-dom';
import { useGetCatalogQuery } from 'services/dtn';
import { DTN_TILE_PROVIDER_ENABLED } from 'configs/endpoints';

import { createLogger } from 'services/debugLogger';
import { parseEnvSupervisorProduct } from '../../utils';
import { connectionTimeout } from '../../configs/initialValues';

const DTN_WORKSPACE_NAME = 'DTN';

const logger = createLogger('ProductsContext');

interface ProductsContextValues {
    selectedProducts: Product[],
    referenceProduct?: Product,
    workspaces: Workspace[],
    activeProducts: number[],
    addNewProduct: (name: string, layer: Layer) => void,
    editProduct: (product: Product) => void,
    editProductTime: (index: number, time: Dayjs | undefined) => void,
    deleteProduct: (index: number) => void,
    activateProduct: (index: number) => void,
    deactivateProduct: (index: number) => void,
    loadingWorkspaces: boolean,
    isProductActive: (product: Product) => boolean,
    capabilities?: WMSCapabilities,
    setCapabilities: Dispatch<SetStateAction<WMSCapabilities| undefined>>,
    setSelectedProducts: Dispatch<SetStateAction<Product[]>>,
    getProductById: (id: number) => Product | undefined,
    activeProductsTimes: Record<number, Dayjs | undefined>
    moveProduct: (oldIndex: number, newIndex: number) => void
    setActiveProductsTimes: Dispatch<SetStateAction<Record<number, Dayjs | undefined> | never>>,
}
interface ProductsProviderProps {
    children: ReactNode
}

const ProductsContext = React.createContext<ProductsContextValues>({
  selectedProducts: [],
  referenceProduct: undefined,
  workspaces: [],
  activeProducts: [],
  addNewProduct: () => {},
  editProduct: () => {},
  editProductTime: () => {},
  deleteProduct: () => {},
  activateProduct: () => {},
  deactivateProduct: () => {},
  loadingWorkspaces: false,
  isProductActive: (product: Product) => false,
  capabilities: undefined,
  setCapabilities: () => {},
  setSelectedProducts: () => {},
  getProductById: () => undefined,
  activeProductsTimes: {},
  moveProduct: () => {},
  setActiveProductsTimes: () => {},
});

// eslint-disable-next-line max-len, no-underscore-dangle
const fixedProducts = parseEnvSupervisorProduct((window as any)._env_.REACT_APP_SUPERVISOR_PRODUCTS);

function ProductsProvider({ children }: ProductsProviderProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const storedProductsList = localStorage.getItem('storedProductsList');
  const {
    data: workspaces, isFetching: loadingWorkspaces, refetch, error,
  } = useGetWorkspacesQuery({});

  const { data: dtnCatalog } = useGetCatalogQuery(undefined, { skip: !DTN_TILE_PROVIDER_ENABLED });

  const dtnWorkspace: Workspace | null = useMemo(() => {
    if (!dtnCatalog?.layers?.length) return null;
    return {
      name: DTN_WORKSPACE_NAME,
      href: 'dtn',
      layers: dtnCatalog.layers.map((dtnLayer) => ({
        name: dtnLayer.label,
        href: 'dtn',
        path: dtnLayer.id,
        type: 'dtn',
      } as GeoLayer)),
    };
  }, [dtnCatalog]);

  const allWorkspaces: Workspace[] = useMemo(
    () => [...(workspaces || []), ...(dtnWorkspace ? [dtnWorkspace] : [])],
    [workspaces, dtnWorkspace],
  );
  const [capabilities, setCapabilities] = useState<WMSCapabilities>();
  const retryIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const parseStoredProductList = (storedProductList: string) => {
    const jsonList = JSON.parse(storedProductList);
    const list: Product[] = jsonList.map((product: Product) => new Product({
      ...product,
      currentDate: undefined,
    }));
    return list;
  };
  const [selectedProducts, setSelectedProducts] = useState<Product[]>(storedProductsList && !location.pathname.includes('supervisor')
    ? parseStoredProductList(storedProductsList)
    : []);
  const [activeProducts, setActiveProducts] = useState<number[]>([]);
  const [activeProductsTimes,
    setActiveProductsTimes] = useState<Record<number, Dayjs | undefined> | never>({});
  const [referenceProduct, setReferenceProduct] = useState(selectedProducts[0]);

  const getProductById = useCallback((index: number) => selectedProducts
    .find((product) => product.index === index), [selectedProducts]);

  const requestCapabilities = useCallback(async () => {
    try {
      const caps = await getCapabilities();
      if (caps) {
        setCapabilities(caps);
        if (retryIntervalRef.current) {
          clearInterval(retryIntervalRef.current);
          retryIntervalRef.current = null;
        }
      }
    } catch (e) {
      logger.error('Error obtaining capabilities', e);

      if (!retryIntervalRef.current) {
        retryIntervalRef.current = setInterval(async () => {
          try {
            const caps = await getCapabilities();
            if (caps) {
              setCapabilities(caps);
              if (retryIntervalRef.current) {
                clearInterval(retryIntervalRef.current);
                retryIntervalRef.current = null;
              }
            }
          } catch (retryError) {
            logger.error('Error in retrying capabilities', retryError);
          }
        }, connectionTimeout);
      }
    }
  }, []);

  const addProducts = useCallback(async (products: Product[]) => {
    setSelectedProducts((prev) => [...prev,
      ...products.map((product, index) => new Product({
        ...product,
        index: prev.length + index,
      }))]);
  }, []);

  const addNewProduct = useCallback(async (value: string, layer: GeoLayer) => {
    if (layer.href === 'dtn') {
      addProducts([new Product({
        index: 0,
        internalName: layer.path || layer.name, // DTN layerId (e.g. "eu-contoured-radar-1km")
        workspace: DTN_WORKSPACE_NAME,
        name: layer.name, // human-readable label
        currentStyle: '',
        layer,
        provider: 'dtn',
      })]);
      return;
    }

    const splittedValue = value.split(':');
    const workspace = splittedValue[0].replace('-', ' ');
    addProducts([new Product({
      index: 0,
      internalName: value,
      workspace,
      name: splittedValue[1].replace('-', ' '),
      currentStyle: layer?.defaultStyle?.name || '',
      layer,
    })]);
  }, [addProducts]);

  const editProduct = useCallback((product: Product) => {
    setSelectedProducts((prev) => prev.map((p, i) => {
      if (i === product.index) {
        return new Product({
          ...product,
        });
      }
      return p;
    }));
  }, []);

  const editProductTime = useCallback((index: number, time: Dayjs | undefined) => {
    setActiveProductsTimes((prev) => ({
      ...prev,
      [index]: time,
    }));
  }, []);

  const deleteProduct = useCallback((index: number) => {
    setSelectedProducts((prev) => {
      const newList = prev
        .filter((p) => p.index !== index)
        .map((p, i) => new Product({ ...p, index: i }));
      return newList;
    });
    setActiveProducts((prev) => {
      const arrangedList = (new Array(4)).fill(undefined);
      prev.forEach((i) => {
        arrangedList[i] = i;
      });
      return arrangedList
        .filter((i, y) => y !== index)
        .map((i, z) => { if (i) { return z; } return i; })
        .filter((i) => i !== undefined);
    });
    setActiveProductsTimes((prev) => {
      const arrangedList = (new Array(4)).fill(undefined);
      Object.keys(prev).forEach((i:string) => {
        const z = parseInt(i, 10);
        arrangedList[z] = prev[z];
      });
      return arrangedList
        .filter((i, y) => y !== index)
        .reduce((acc, cur, i) => {
          if (cur) {
            acc[i] = cur;
          }
          return acc;
        }, {});
    });
  }, []);

  const activateProduct = useCallback((index: number) => {
    setActiveProducts((prev) => [
      ...prev,
      index,
    ].sort());
  }, []);

  const moveProduct = useCallback((oldIndex: number, newIndex: number) => {
    setSelectedProducts((prev) => {
      const newList = arrayMove(prev, oldIndex, newIndex)
        .map((p, i) => new Product({ ...p, index: i })) as Product[];
      return newList;
    });

    if (activeProducts.length > 0) {
      setActiveProducts((prev) => {
        let clonedArray = new Array(4);
        prev.forEach((item) => { clonedArray[item] = item; });
        clonedArray = arrayMove(clonedArray, oldIndex, newIndex);
        clonedArray = clonedArray.map((item, index) => {
          if (item !== undefined) { return index; } return item;
        }).filter((item) => item !== undefined);
        return clonedArray;
      });

      setActiveProductsTimes((prev) => {
        const auxArray = new Array(4).fill(undefined);
        let newTimesArray = new Array(4);
        auxArray.forEach((_, index) => {
          if (prev[index] === undefined) {
            newTimesArray[index] = undefined;
          } else {
            newTimesArray[index] = prev[index];
          }
        });

        newTimesArray = arrayMove(newTimesArray, oldIndex, newIndex);
        return newTimesArray.reduce((acc, cur, index) => {
          if (cur !== undefined) {
            acc[index] = cur;
          }
          return acc;
        }, {});
      });
    }
  }, [activeProducts]);

  const deactivateProduct = (index: number) => {
    setActiveProducts((prev) => prev.filter((productIndex) => productIndex !== index));
  };

  const isProductActive = useCallback((product: Product) => activeProducts
    .some((index) => index === product.index), [activeProducts]);

  useEffect(() => {
    requestCapabilities();
  }, [requestCapabilities]);

  // The capabilities retry interval lives in a ref and only stops on success: stop it on unmount.
  useEffect(() => () => {
    if (retryIntervalRef.current) {
      clearInterval(retryIntervalRef.current);
      retryIntervalRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (error) {
      const interval = setInterval(() => {
        refetch();
      }, connectionTimeout);
      return () => clearInterval(interval);
    }
    return () => {};
  }, [error, refetch]);

  const contextValues = useMemo(() => ({
    workspaces: allWorkspaces,
    activeProducts,
    referenceProduct,
    selectedProducts,
    addNewProduct,
    editProduct,
    deleteProduct,
    activateProduct,
    deactivateProduct,
    loadingWorkspaces,
    isProductActive,
    capabilities,
    setCapabilities,
    getProductById,
    setSelectedProducts,
    editProductTime,
    activeProductsTimes,
    moveProduct,
    setActiveProductsTimes,
  }), [
    allWorkspaces, activeProducts, referenceProduct, selectedProducts,
    addNewProduct, editProduct, deleteProduct,
    activateProduct, loadingWorkspaces,
    isProductActive, capabilities, getProductById, editProductTime,
    activeProductsTimes, moveProduct, setActiveProductsTimes,
  ]);

  useEffect(() => {
    localStorage.setItem('storedProductsList', JSON.stringify(selectedProducts));
  }, [selectedProducts]);

  useEffect(() => {
    if (selectedProducts[0]?.name !== referenceProduct?.name) {
      setReferenceProduct(selectedProducts[0]);
    }
  }, [referenceProduct, selectedProducts]);

  useEffect(() => {
    function findLayersById(ids: string[], workspaces: Workspace[]): GeoLayer[] {
      const idToObjectMap = new Map<string, GeoLayer>();

      workspaces.forEach((workspace) => {
        workspace.layers?.forEach((layer) => {
          if (ids.includes(`${workspace.name}:${layer.name}`)) {
            idToObjectMap.set(`${workspace.name}:${layer.name}`, layer);
          }
        });
      });

      return ids.map((id) => idToObjectMap.get(id)!);
    }
    if (location.pathname.includes('supervisor') && workspaces && fixedProducts) {
      const products = findLayersById(fixedProducts, workspaces);
      setSelectedProducts(products.map((p, i) => {
        const splittedValue = fixedProducts[i].split(':');
        const workspace = splittedValue[0].replace('-', ' ');
        return new Product({
          index: i,
          internalName: fixedProducts[i],
          workspace,
          name: splittedValue[1].replace('-', ' '),
          currentStyle: p?.defaultStyle?.name || '',
          // currentOpacity: 100,
          layer: p,
        });
      }));
    }

    if (location.pathname.includes('supervisor') && !fixedProducts) {
      navigate('/app/map');
    }
  }, [location.pathname, navigate, workspaces]);

  useEffect(() => {
    if (selectedProducts && location.pathname.includes('supervisor')) {
      selectedProducts.forEach(() => {
        setActiveProducts([0, 1, 2, 3]);
      });
    }
  }, [location.pathname, selectedProducts]);

  // Limpiar el intervalo al desmontar el componente
  useEffect(() => () => {
    if (retryIntervalRef.current) {
      clearInterval(retryIntervalRef.current);
    }
  }, []);

  return (
    <ProductsContext.Provider value={contextValues}>
      {children}
    </ProductsContext.Provider>
  );
}

export { ProductsProvider };
export default ProductsContext;
